/* Copyright (c) 2023 Xperi. All Rights Reserved.  */

import { Payload, QoE } from '@tivocorp/tivo-webapp-loggers';
import { AiringOffer } from 'client-code/airing';
import { TransportEncodingType } from 'client-code/video-provider-configurations';

import {
    ASSETBOUNDS,
    DASH_TRANSPORT_STREAM,
    HLS_TRANSPORT_STREAM,
    MEDIA_HEALTH_EVENTS_INTERVAL,
    MILLISECONDS_IN_A_SECOND,
    SMOOTH_TRANSPORT_STREAM,
    STREAM_TYPE,
    VIDEO_PLAYER,
} from '../../../config';
import { AppUtils } from '../../AppUtils';
import { Constants } from '../../constants';
import { DeviceModel } from '../../device/DeviceModel';
import { Channel } from '../../entities/Channel';
import { LiveTvError } from '../../error';
import { PlaybackContentType } from '../../playback/enums';
import { TOfferPreviewCustomData } from '../../types';
import { UniqueIdentifier } from '../../utils/UniqueIdentifier';
import { PlaybackRestrictionType } from '../../videoplayer/enums';
import { IPlayerStats } from '../../videoplayer/interfaces/IPlayerStats';
import { ISeekRange } from '../../videoplayer/interfaces/ISeekRange';
import { IVariantTrack } from '../../videoplayer/interfaces/IVariantTrack';
import { Log } from '../Log';
import { IQoEConfiguration } from './IQoEConfiguration';
import { IQoEContent } from './IQoEContent';
import { IQoEMetrics } from './IQoEMetrics';
import { IQoEState } from './IQoEState';
import { IQoEStatus } from './IQoEStatus';
import { QoEConnectionModeType } from './QoEConnectionModeType';
import { QoEContainerFormatType } from './QoEContainerFormatType';
import { QoERegionModeType } from './QoERegionModeType';
import { QoEStreamingProtocolType } from './QoEStreamingProtocolType';
import { VideoStreamingState } from './VideoStreamingState';

const crypto = require('crypto-browserify');

type Profile = {
    bandwidth: number;
    height: number;
    playTime: number;
    ratio: number;
    width: number;
};

const SESSION_TYPE = {
    live: 'LiveTv',
    socu: 'Socu',
    dvr: 'Dvr',
};

const VIDEO_SOURCE = {
    ndvr: 'ndvr',
    iptv: 'IPTV',
};

export type EndReasonData = {
    type: string;
    payload: IQoEStatus;
};

type PlayerInfo = {
    stats: IPlayerStats;
    seekRange: ISeekRange;
    variantTracks: IVariantTrack[];
    currentTime: number;
    selectedAudioTrack?: IVariantTrack;
    bufferedInfo: TimeRanges;
};

export class QoEAppLogger {
    private static TAG = 'QoEAppLogger';

    public static sessionId: string = '';

    private static trickplayStartTime: number = 0;

    public static PlaybackEndReasonType = {
        ADULT_CONTENT: 'ADULT_CONTENT',
        APP_EXIT: 'APP_EXIT',
        BLACKLIST: 'BLACKLIST',
        CHANNEL_CHANGE: 'CHANNEL_CHANGE',
        DEVICE_RESTRICTED: 'DEVICE_RESTRICTED',
        END_OF_CONTENT: 'END_OF_CONTENT',
        ENDED_BY_USER: 'ENDED_BY_USER',
        IN_HOME_LOCATION: 'IN_HOME_LOCATION',
        LOCATION_ERROR: 'LOCATION_ERROR',
        LOST_NETWORK: 'LOST_NETWORK',
        OUT_OF_HOME: 'OUT_OF_HOME',
        OUT_OF_REGION: 'OUT_OF_REGION',
        PLAYER_ERROR: 'PLAYER_ERROR',
        SESSION_ERROR: 'SESSION_ERROR',
        UNSUBSCRIBED: 'UNSUBSCRIBED',
        VISIBILITY_BEHIND: 'VISIBILITY_BEHIND',
        PLAYLIST_URI_FROM_SESSION_MANAGER: 'PLAYLIST_URI_FROM_SESSION_MANAGER',
    };

    public static ErrorLocValues = {
        CLIENT: 'CLIENT',
        CONTENT: 'CONTENT',
        LOCATION: 'LOCATION',
        SESSION: 'SESSION',
    };

    public static StreamEndReason = {
        AUDIO_TRACK_CHANGED: 'AUDIO_TRACK_CHANGED',
    };

    public static PlaybackEndStatus = {
        CANCEL: 'CANCEL',
        SUCCESS: 'SUCCESS',
        FAIL: 'FAIL',
    };

    public static mediaStatsDefautValues: IQoEMetrics = {
        avgVideoBitrate: 0,
        avgNetworkBitrate: 0,
        networkBitrate: 0,
        playPosition: 0,
        profileShiftCount: 0,
        videoBufferAvailable: 0,
        videoBufferingTime: 0,
        videoBufferingCount: 0,
        videoBitrate: 0,
        videoFramesPresented: 0,
        videoFramesDropped: 0,
        videoPlayingTime: 0,
        videoPlayingCount: 0,
        videoPausingTime: 0,
        videoPausingCount: 0,
        videoPlayingStartTime: 0,
        videoStartTime: 0,
        videoStallingCount: 0,
        videoStallingTime: 0,
        videoTrickplayingTime: 0,
        videoTrickplayingCount: 0,
    };

    public static mediaStats: IQoEMetrics = {
        ...QoEAppLogger.mediaStatsDefautValues,
    };

    private static contentDefaultValues: IQoEContent = {
        audioCodec: '',
        channelId: '',
        channelCallSign: '',
        channelName: '',
        channelNum: '',
        collectionId: '',
        collectiontitle: '',
        contentId: '',
        contentTitle: '',
        maxRes: '',
        videoCodec: '',
    };

    private static content: IQoEContent = {
        ...QoEAppLogger.contentDefaultValues,
    };

    private static stateDefaultValues: IQoEState = {
        startTime: 0,
        streamingState: VideoStreamingState.STREAMING_STATE_PREFLIGHT,
    };

    private static state: IQoEState = {
        ...QoEAppLogger.stateDefaultValues,
    };

    private static configuration: IQoEConfiguration = {
        assetBounds: ASSETBOUNDS,
        connectionMode: QoEConnectionModeType.UNKNOWN,
        connection: '',
        regionMode: QoERegionModeType.UNKNOWN,
        sessionType: SESSION_TYPE.live,
        streamType: STREAM_TYPE,
        streamingProtocol: '',
        videoPlayer: VIDEO_PLAYER,
        videoSource: VIDEO_SOURCE.iptv,
    };

    private static endStatus: IQoEStatus = {
        errorCode: '',
        errorLoc: '',
        errorMsg: '',
        errorNative: '',
        errorType: '',
        errorUrl: '',
        userFinalCodes: '',
        userTerminalCodes: '',
        doneReason: '',
        status: '',
        reason: '',
    };

    public static PlayerEvents = {
        PLAY: 'play',
        PAUSE: 'pause',
        BUFFERING: 'buffering',
        STALLED: 'stalled',
        CHANGED_AD_TRACK: 'nonCollectibleAction',
        RATE_CHANGE: 'profileShiftCount',
    };

    public static ErrorType = {
        ERROR_STATUS: 'ERROR_STATUS',
        REASON: 'REASON',
        USER_CODE: 'USER_CODE',
    };

    public static PlaybackEndReason = {
        DEFAULT: '',
        USER_CLOSED_THE_VIDEO_WINDOW: 'User closed the video window',
        USER_EXITED_FROM_LIVE_TV: 'User exited from live TV',
        USER_MOVE_TO_LIVE_TV_FROM_GUIDE_AND_CHANNEL_WAS_CHANGED:
            'User move to Live TV from Guide and channel was changed',
        USER_TUNED_TO_NEXT_CHANNEL: 'User tuned to next channel',
        USER_TUNED_TO_OTHER_CHANNEL_BY_ENTERING_CHANNEL_NUMBER:
            'User tuned to other channel by entering channel number',
        USER_TUNED_TO_PREVIOUS_CHANNEL: 'User tuned to previous channel',
    };

    private static previousPlayerEvent: Event | null = null;

    private static isProgramBoundary: boolean;

    public static isWatchContentStarted = false;

    public static isStreamingStarted = false;

    public static profiles: Profile[] = [];

    public static watchContentStartTime: number = 0;

    public static mediaHealthStartIntervalTime: number = 0;

    public static isAdaptationEventFirstTime: boolean = false;

    public static isErrorOccurred: boolean = false;

    public static qoeMediaHealthTimer;

    public static sessionCount = 0;

    public static watchContentSessionCount = 0;

    public static streamingSessionCount = 0;

    private static watchContentEndReason: string = this.PlaybackEndReason.DEFAULT;

    public static lastTunedChannel: string | undefined = '';

    private static channel: Channel;

    private static airingOffers: AiringOffer[];

    private static isUserTriggerAction: boolean = false;

    private static estimatedBandwidthList: number[] = [];

    private static playerInfo: PlayerInfo;

    // *****************************************************************

    public static createAppSessionId() {
        // Application execution session identifier
        QoEAppLogger.sessionId = crypto
            .createHash('sha1')
            .update(UniqueIdentifier.uuid4(), 'utf-8')
            .digest('hex')
            .substring(0, 20);
    }

    public static getIsUserTriggerAction() {
        return QoEAppLogger.isUserTriggerAction;
    }

    public static setIsUserTriggerAction(value: boolean) {
        QoEAppLogger.isUserTriggerAction = value;
    }

    /** *****************************************************************
     * ////////////////////// QoE Events //////////////////////////////////////////
     * applicationStart
     * watchContentStart
     * streamingStart
     * sessionCreated
     * videoPlayerOpenStream
     * firstFrame
     * mediaHealth
     * streamingEnd
     * watchContentEnd
     */

    public static logApplicationStart = () => {
        QoE.logApplicationStart();
    };

    public static updateWatchContentUniqueId = (payload: Payload) => {
        payload.add(
            'watchContentTransaction',
            `${QoEAppLogger.sessionId}_${QoEAppLogger.watchContentSessionCount}`,
        );
    };

    public static updateStreamingSessionUniqueId = (payload: Payload) => {
        payload.add(
            'streamingSessionTransaction',
            `${QoEAppLogger.sessionId}_${QoEAppLogger.streamingSessionCount}`,
        );
    };

    public static setPlayerInfo = (playerInfo: PlayerInfo) => {
        QoEAppLogger.playerInfo = playerInfo;
    };

    public static getBufferedInfo = (): number => {
        const { bufferedInfo, currentTime } = QoEAppLogger.playerInfo || {};
        let bufferedAhead = 0;

        for (let i = 0; bufferedInfo && i < bufferedInfo.length; i += 1) {
            const start = bufferedInfo.start(i);
            const end = bufferedInfo.end(i);

            if (end > currentTime) {
                bufferedAhead += Math.max(0, end - Math.max(start, currentTime));
            }
        }
        return bufferedAhead;
    };

    public static logWatchContentStart = (
        playbackContentType: PlaybackContentType | undefined,
        channel?: Channel,
        airingOffer?: AiringOffer[],
        vodPreviewData?: TOfferPreviewCustomData,
    ) => {
        switch (playbackContentType) {
            case PlaybackContentType.STARTOVER_RESUME:
            case PlaybackContentType.STARTOVER_BEGINNING:
            case PlaybackContentType.CATCHUP_BEGINNING:
            case PlaybackContentType.CATCHUP_RESUME:
                this.configuration.sessionType = SESSION_TYPE.socu;
                break;
            case PlaybackContentType.NDVR_LIVE:
            case PlaybackContentType.NDVR_RECORDED:
                this.configuration.sessionType = SESSION_TYPE.dvr;
                this.configuration.videoSource = VIDEO_SOURCE.ndvr;
                break;
            default:
                this.configuration.sessionType = SESSION_TYPE.live;
                break;
        }

        if (channel) {
            QoEAppLogger.channel = channel;
            if (airingOffer && airingOffer?.length > 0) {
                QoEAppLogger.lastTunedChannel = channel.channelNumber;
                QoEAppLogger.airingOffers = airingOffer;
            }
        }

        if (vodPreviewData) {
            QoEAppLogger.updateContentPayload(QoEAppLogger.channel, [], vodPreviewData);
        } else if (QoEAppLogger.airingOffers)
            QoEAppLogger.updateContentPayload(QoEAppLogger.channel, QoEAppLogger.airingOffers);
        if (!QoEAppLogger.isWatchContentStarted) {
            QoEAppLogger.watchContentSessionCount = QoEAppLogger.sessionCount;
            QoEAppLogger.sessionCount += 1;
            if (QoEAppLogger.isProgramBoundary) {
                QoEAppLogger.state.streamingState = VideoStreamingState.STREAMING_STATE_STREAMING;
            } else {
                QoEAppLogger.state.streamingState = VideoStreamingState.STREAMING_STATE_PREFLIGHT;
            }
            QoEAppLogger.state.startTime = Date.now() / Constants.MILLISECOND_IN_ONE_SECOND;
            QoEAppLogger.watchContentStartTime =
                QoEAppLogger.state.startTime * Constants.MILLISECOND_IN_ONE_SECOND;
            let payload = QoEAppLogger.getContentPayload();
            payload = payload.merge(QoEAppLogger.getPlayerStatePayload());
            payload = payload.merge(QoEAppLogger.getPlayerConfigurationPayload());
            delete payload.getProperties().streamingSessionTransaction;
            QoEAppLogger.updateWatchContentUniqueId(payload);
            QoE.logWatchContentStart(payload);
            QoEAppLogger.isProgramBoundary = false;
            QoEAppLogger.isWatchContentStarted = true;
            if (QoEAppLogger.isErrorOccurred) {
                QoEAppLogger.isErrorOccurred = false;
                if (QoEAppLogger.isStreamingStarted) {
                    QoEAppLogger.logStreamingEndEvent();
                }
                QoEAppLogger.logWatchContentEnd();
            }
        }
    };

    public static logStreamingStartEvent = (audioTrack?: IVariantTrack) => {
        QoEAppLogger.setIsUserTriggerAction(true);
        QoEAppLogger.streamingSessionCount = QoEAppLogger.sessionCount;
        QoEAppLogger.sessionCount += 1;
        QoEAppLogger.updateContentPayloadFromStream(audioTrack);
        let payload = QoEAppLogger.getContentPayload();
        payload = payload.merge(QoEAppLogger.getPlayerStatePayload());
        payload = payload.merge(QoEAppLogger.getPlayerConfigurationPayload());
        delete payload.getProperties().watchContentTransaction;
        QoEAppLogger.updateStreamingSessionUniqueId(payload);
        QoE.logStreamingStart(payload);

        QoEAppLogger.isStreamingStarted = true;
    };

    public static logSessionCreatedEvent = () => {
        QoEAppLogger.state.streamingState = VideoStreamingState.STREAMING_STATE_STARTING;
        const payload = QoEAppLogger.getPlayerStatePayload();
        QoEAppLogger.updateWatchContentUniqueId(payload);
        QoEAppLogger.updateStreamingSessionUniqueId(payload);
        QoE.logSessionCreated(payload);
    };

    public static logVideoPlayerOpenStreamEvent = () => {
        const payload = QoEAppLogger.getPlayerStatePayload();
        QoEAppLogger.updateWatchContentUniqueId(payload);
        QoEAppLogger.updateStreamingSessionUniqueId(payload);
        QoE.logVideoPlayerOpenStream(payload);
    };

    public static logFirstFrameEvent = () => {
        const now = Date.now();
        // update firstFrame attributes
        if (
            QoEAppLogger.state.startTime &&
            now > QoEAppLogger.state.startTime * Constants.MILLISECOND_IN_ONE_SECOND
        ) {
            QoEAppLogger.mediaStats.videoStartTime =
                now - QoEAppLogger.state.startTime * Constants.MILLISECOND_IN_ONE_SECOND;
            QoEAppLogger.mediaStats.videoPlayingStartTime = QoEAppLogger.mediaStats.videoStartTime;
        }
        if (!QoEAppLogger.mediaStats.videoPlayingCount) {
            QoEAppLogger.mediaStats.videoPlayingCount = 1;
        }
        // set streamingState to streaming from starting
        QoEAppLogger.state.streamingState = VideoStreamingState.STREAMING_STATE_STREAMING;

        const payload = QoEAppLogger.getPlayerStatePayload();
        QoEAppLogger.updateWatchContentUniqueId(payload);
        QoEAppLogger.updateStreamingSessionUniqueId(payload);
        QoE.logFirstFrame(payload);
        QoEAppLogger.isErrorOccurred = false;
    };

    public static updateProfilePlayingTime = () => {
        if (QoEAppLogger.profiles.length) {
            QoEAppLogger.profiles.forEach((profile) => {
                if (profile.playTime > 0) {
                    QoEAppLogger.mediaStats[
                        `profilePlayingTime_${profile.width}x${profile.height}:${profile.ratio}@${profile.bandwidth}`
                    ] = profile.playTime;
                }
            });
        }
    };

    public static resetProfilePlayingTime = () => {
        QoEAppLogger.isAdaptationEventFirstTime = false;
        QoEAppLogger.mediaHealthStartIntervalTime = 0;
        QoEAppLogger.mediaStats.profileShiftCount = 0;

        if (QoEAppLogger.profiles.length) {
            QoEAppLogger.profiles.forEach((profile) => {
                delete QoEAppLogger.mediaStats[
                    `profilePlayingTime_${profile.width}x${profile.height}:${profile.ratio}@${profile.bandwidth}`
                ];
            });
        }
        QoEAppLogger.profiles = [];
    };

    public static clearMediaStatsAttributes = () => {
        QoEAppLogger.mediaStats = {
            ...QoEAppLogger.mediaStatsDefautValues,
        };
        QoEAppLogger.estimatedBandwidthList = [];
    };

    public static clearContentAttributes = () => {
        QoEAppLogger.content = {
            ...QoEAppLogger.contentDefaultValues,
            drmType: QoEAppLogger.content.drmType,
            containerFmt: QoEAppLogger.content.containerFmt,
        };
    };

    public static clearStateAttributes = () => {
        QoEAppLogger.state = {
            ...QoEAppLogger.stateDefaultValues,
            videoPlayerURL: QoEAppLogger.state.videoPlayerURL,
        };
    };

    public static clearAllAttributes = () => {
        QoEAppLogger.clearMediaStatsAttributes();
        QoEAppLogger.clearContentAttributes();
        QoEAppLogger.clearStateAttributes();
    };

    public static logMediaHealthEvent = () => {
        const { stats, selectedAudioTrack, currentTime } = QoEAppLogger.playerInfo || {};
        if (stats && !Number.isNaN(stats.playTime)) {
            QoEAppLogger.updateAllHealthStatsMetrics(stats);
            QoEAppLogger.updateProfileTime(stats);
            QoEAppLogger.updateProfilePlayingTime();
            QoEAppLogger.updateContentPayloadFromStream(selectedAudioTrack);
            QoEAppLogger.updateMetricsPlayPositionAndBuffer(currentTime);
        }
        let payload = QoEAppLogger.getContentPayload();
        QoEAppLogger.updateWatchContentUniqueId(payload);
        QoEAppLogger.updateStreamingSessionUniqueId(payload);
        payload = payload.merge(QoEAppLogger.getPlayerStatePayload());
        payload = payload.merge(QoEAppLogger.getMetricsPayload());
        payload = payload.merge(QoEAppLogger.getPlayerConfigurationPayload());
        QoE.logMediaHealth(payload);
    };

    public static startMediaHealthTimer = () => {
        QoEAppLogger.state.streamingState = VideoStreamingState.STREAMING_STATE_STREAMING;
        QoEAppLogger.mediaHealthStartIntervalTime = Date.now();
        QoEAppLogger.logMediaHealthEvent();
        QoEAppLogger.qoeMediaHealthTimer = setInterval(() => {
            QoEAppLogger.mediaStats.profileShiftCount =
                QoEAppLogger.playerInfo?.stats?.switchHistory.length - 1 || 0;
            QoEAppLogger.logMediaHealthEvent();
            QoEAppLogger.mediaHealthStartIntervalTime = Date.now();
        }, MEDIA_HEALTH_EVENTS_INTERVAL);
    };

    public static stopMediaHealthTimer = () => {
        clearInterval(QoEAppLogger.qoeMediaHealthTimer);
    };

    private static updateAndGetStatusPayload = () => {
        let payload = QoEAppLogger.getContentPayload();
        payload = payload.merge(QoEAppLogger.getPlayerStatePayload());
        payload = payload.merge(QoEAppLogger.getMetricsPayload());
        payload = payload.merge(QoEAppLogger.getPlayerConfigurationPayload());
        payload = payload.merge(QoEAppLogger.getEndStatusPayload());
        const filteredProperties = this.getEndStatusPayload().getProperties();
        AppUtils.filterUnknownOrEmptyOrUndefined(<JSON>filteredProperties);
        const tempPayload = new Payload(filteredProperties);
        payload = payload.merge(tempPayload);
        return payload;
    };

    public static logStreamingEndEvent = () => {
        if (!QoEAppLogger.isStreamingStarted) {
            Log.w(
                QoEAppLogger.TAG,
                'Ignored. - Trying to log streamingEnd event without streamingStart ...',
            );
            return;
        }
        const payload = QoEAppLogger.updateAndGetStatusPayload();
        delete payload.getProperties().watchContentTransaction;
        QoEAppLogger.updateStreamingSessionUniqueId(payload);
        QoE.logStreamingEnd(payload);
        // reset streamingStart flag
        QoEAppLogger.isStreamingStarted = false;
    };

    public static logWatchContentEnd = () => {
        if (!QoEAppLogger.isWatchContentStarted) {
            Log.w(
                QoEAppLogger.TAG,
                'Ignored. - Trying to log watchContentEnd event without watchContentStart ...',
            );
            return;
        }
        QoEAppLogger.isErrorOccurred = false;
        const payload = QoEAppLogger.updateAndGetStatusPayload();
        delete payload.getProperties().streamingSessionTransaction;
        QoEAppLogger.updateWatchContentUniqueId(payload);
        QoE.logWatchContentEnd(payload);
        // reset watchContentStart flag
        QoEAppLogger.isWatchContentStarted = false;
        QoEAppLogger.lastTunedChannel = '';
    };

    public static setEndReason = (data: EndReasonData) => {
        switch (data.type) {
            case QoEAppLogger.ErrorType.ERROR_STATUS:
            case QoEAppLogger.ErrorType.REASON:
            case QoEAppLogger.ErrorType.USER_CODE:
                QoEAppLogger.updateStatusPayload(data.payload);
                break;
            default:
        }
    };

    public static logAppExit = (
        stats: IPlayerStats,
        playerCurrentTime: number,
        seekRange: ISeekRange,
    ) => {
        QoEAppLogger.setEndReasonAndLogEvent(
            QoEAppLogger.PlaybackEndReasonType.APP_EXIT,
            {
                type: QoEAppLogger.ErrorType.REASON,
                payload: {
                    status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                    doneReason: QoEAppLogger.PlaybackEndReasonType.APP_EXIT,
                    reason: 'User closed the Application.',
                },
            },
            stats,
            playerCurrentTime,
            seekRange,
        );
    };

    public static logAudioTrackChanged = (
        stats: IPlayerStats,
        playerCurrentTime: number,
        seekRange: ISeekRange,
    ) => {
        QoEAppLogger.setEndReasonAndLogEvent(
            QoEAppLogger.StreamEndReason.AUDIO_TRACK_CHANGED,
            {
                type: QoEAppLogger.ErrorType.REASON,
                payload: {
                    status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                    doneReason: QoEAppLogger.StreamEndReason.AUDIO_TRACK_CHANGED,
                    reason: 'User changed Audio Track.',
                },
            },
            stats,
            playerCurrentTime,
            seekRange,
        );
    };

    public static logChannelChanged = (
        stats: IPlayerStats,
        playerCurrentTime: number,
        seekRange: ISeekRange,
    ) => {
        if (QoEAppLogger.isErrorOccurred) {
            return;
        }
        QoEAppLogger.setEndReasonAndLogEvent(
            QoEAppLogger.PlaybackEndReasonType.CHANNEL_CHANGE,
            {
                type: QoEAppLogger.ErrorType.REASON,
                payload: {
                    status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                    doneReason: QoEAppLogger.PlaybackEndReasonType.CHANNEL_CHANGE,
                    reason: QoEAppLogger.getWatchContentEndReason(),
                },
            },
            stats,
            playerCurrentTime,
            seekRange,
        );
    };

    public static logDifferentURI = (
        stats: IPlayerStats,
        playerCurrentTime: number,
        seekRange: ISeekRange,
    ) => {
        QoEAppLogger.setEndReasonAndLogEvent(
            QoEAppLogger.PlaybackEndReasonType.PLAYLIST_URI_FROM_SESSION_MANAGER,
            {
                type: QoEAppLogger.ErrorType.REASON,
                payload: {
                    status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                    doneReason:
                        QoEAppLogger.PlaybackEndReasonType.PLAYLIST_URI_FROM_SESSION_MANAGER,
                    reason: 'sessionCreate URL is different from tuningUri so restarted playback as there is a mismatch.',
                },
            },
            stats,
            playerCurrentTime,
            seekRange,
        );
    };

    public static logLocationError(
        error: LiveTvError,
        stats: IPlayerStats,
        playerCurrentTime: number,
        seekRange: ISeekRange,
    ) {
        QoEAppLogger.isErrorOccurred = true;
        QoEAppLogger.setEndReasonAndLogEvent(
            QoEAppLogger.PlaybackEndReasonType.LOCATION_ERROR,
            {
                type: QoEAppLogger.ErrorType.ERROR_STATUS,
                payload: {
                    errorCode: error.getCode(),
                    errorLoc: QoEAppLogger.ErrorLocValues.LOCATION,
                    errorType: QoEAppLogger.PlaybackEndReasonType.LOCATION_ERROR,
                    errorNative: JSON.stringify(error),
                    errorMsg: error.getDescription(),
                    doneReason: QoEAppLogger.PlaybackEndReasonType.LOCATION_ERROR,
                },
            },
            stats,
            playerCurrentTime,
            seekRange,
        );
    }

    public static logNetworkLost = (
        stats: IPlayerStats,
        playerCurrentTime: number,
        seekRange: ISeekRange,
    ) => {
        QoEAppLogger.setEndReasonAndLogEvent(
            QoEAppLogger.PlaybackEndReasonType.LOST_NETWORK,
            {
                type: QoEAppLogger.ErrorType.REASON,
                payload: {
                    status: QoEAppLogger.PlaybackEndStatus.CANCEL,
                    doneReason: QoEAppLogger.PlaybackEndReasonType.LOST_NETWORK,
                    reason: 'Network Lost.',
                    errorLoc: 'NETWORK',
                },
            },
            stats,
            playerCurrentTime,
            seekRange,
        );
    };

    private static RestrictionNotificationEvents = {
        [PlaybackRestrictionType.ADULT]: {
            type: QoEAppLogger.PlaybackEndReasonType.ADULT_CONTENT,
            reason: 'User tried to play Adult Content',
        },
        [PlaybackRestrictionType.BLACKLISTED]: {
            type: QoEAppLogger.PlaybackEndReasonType.BLACKLIST,
            reason: 'Device is blacklisted',
        },
        [PlaybackRestrictionType.DEVICE_RESTRICTED]: {
            type: QoEAppLogger.PlaybackEndReasonType.DEVICE_RESTRICTED,
            reason: 'Device is Restricted',
        },
        [PlaybackRestrictionType.OUT_OF_HOME]: {
            type: QoEAppLogger.PlaybackEndReasonType.OUT_OF_HOME,
            reason: 'Device is Out of Home',
        },
        [PlaybackRestrictionType.OUT_OF_REGION]: {
            type: QoEAppLogger.PlaybackEndReasonType.OUT_OF_REGION,
            reason: 'Device is Out of Region',
        },
        [PlaybackRestrictionType.UNSUBSCRIBED]: {
            type: QoEAppLogger.PlaybackEndReasonType.UNSUBSCRIBED,
            reason: 'User tried to play unsubscribed content',
        },
    };

    public static logVideoPlayerRestrictionNotificationEvent = (
        restriction: PlaybackRestrictionType,
        errorObj: LiveTvError,
        stats: IPlayerStats,
        playerCurrentTime: number,
        seekRange: ISeekRange,
    ) => {
        if (restriction === PlaybackRestrictionType.INHOME_LOCATION_ERROR) {
            QoEAppLogger.logLocationError(errorObj, stats, playerCurrentTime, seekRange);
            return;
        }
        if (restriction in QoEAppLogger.RestrictionNotificationEvents) {
            const { type, reason } = QoEAppLogger.RestrictionNotificationEvents[restriction];
            QoEAppLogger.isErrorOccurred = true;
            QoEAppLogger.setEndReasonAndLogEvent(
                type,
                {
                    type: QoEAppLogger.ErrorType.USER_CODE,
                    payload: {
                        status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                        reason,
                        userTerminalCodes: errorObj.getCode(),
                        userFinalCodes: errorObj.getCode(),
                        doneReason: type,
                    },
                },
                stats,
                playerCurrentTime,
                seekRange,
            );
        }
    };

    public static logPlaybackError = (
        error: LiveTvError,
        stats: IPlayerStats,
        playerCurrentTime: number,
        seekRange: ISeekRange,
    ) => {
        QoEAppLogger.isErrorOccurred = true;
        QoEAppLogger.setEndReasonAndLogEvent(
            QoEAppLogger.PlaybackEndReasonType.PLAYER_ERROR,
            {
                type: QoEAppLogger.ErrorType.ERROR_STATUS,
                payload: {
                    errorCode: error.getCode(),
                    errorLoc: QoEAppLogger.ErrorLocValues.CONTENT,
                    errorType: QoEAppLogger.PlaybackEndReasonType.PLAYER_ERROR,
                    errorNative: JSON.stringify(error),
                    errorMsg: error.getDescription(),
                    doneReason: QoEAppLogger.PlaybackEndReasonType.PLAYER_ERROR,
                    status: QoEAppLogger.PlaybackEndStatus.CANCEL,
                    reason: 'Player Error',
                    userAgent: navigator.userAgent,
                },
            },
            stats,
            playerCurrentTime,
            seekRange,
        );
    };

    public static logPlaybackStopped = (
        stats: IPlayerStats,
        playerCurrentTime: number,
        seekRange: ISeekRange,
    ) => {
        if (QoEAppLogger.isErrorOccurred) {
            return;
        }
        QoEAppLogger.setEndReasonAndLogEvent(
            QoEAppLogger.PlaybackEndReasonType.ENDED_BY_USER,
            {
                type: QoEAppLogger.ErrorType.REASON,
                payload: {
                    status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                    doneReason: QoEAppLogger.PlaybackEndReasonType.ENDED_BY_USER,
                    reason: QoEAppLogger.getWatchContentEndReason(),
                },
            },
            stats,
            playerCurrentTime,
            seekRange,
        );
    };

    public static logProgramEnded = (
        stats: IPlayerStats,
        playerCurrentTime: number,
        seekRange: ISeekRange,
    ) => {
        QoEAppLogger.setEndReasonAndLogEvent(
            QoEAppLogger.PlaybackEndReasonType.END_OF_CONTENT,
            {
                type: QoEAppLogger.ErrorType.REASON,
                payload: {
                    status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                    doneReason: QoEAppLogger.PlaybackEndReasonType.END_OF_CONTENT,
                    reason: 'Program Ended',
                },
            },
            stats,
            playerCurrentTime,
            seekRange,
        );
    };

    public static logSessionError = (
        error: LiveTvError,
        stats: IPlayerStats,
        playerCurrentTime: number,
        seekRange: ISeekRange,
    ) => {
        QoEAppLogger.isErrorOccurred = true;
        QoEAppLogger.setEndReasonAndLogEvent(
            QoEAppLogger.PlaybackEndReasonType.SESSION_ERROR,
            {
                type: QoEAppLogger.ErrorType.ERROR_STATUS,
                payload: {
                    errorCode: error.getCode(),
                    errorLoc: QoEAppLogger.ErrorLocValues.SESSION,
                    errorType: QoEAppLogger.PlaybackEndReasonType.SESSION_ERROR,
                    errorNative: JSON.stringify(error),
                    errorMsg: error.getDescription(),
                    doneReason: QoEAppLogger.PlaybackEndReasonType.SESSION_ERROR,
                },
            },
            stats,
            playerCurrentTime,
            seekRange,
        );
    };

    public static logVisibilityBackground = (
        stats: IPlayerStats,
        playerCurrentTime: number,
        seekRange: ISeekRange,
    ) => {
        QoEAppLogger.setEndReasonAndLogEvent(
            QoEAppLogger.PlaybackEndReasonType.VISIBILITY_BEHIND,
            {
                type: QoEAppLogger.ErrorType.REASON,
                payload: {
                    status: QoEAppLogger.PlaybackEndStatus.SUCCESS,
                    doneReason: QoEAppLogger.PlaybackEndReasonType.VISIBILITY_BEHIND,
                    reason: 'User switched to some other application.',
                },
            },
            stats,
            playerCurrentTime,
            seekRange,
        );
    };

    public static setEndReasonAndLogEvent = (
        reason: string,
        data: EndReasonData,
        stats: IPlayerStats,
        playerCurrentTime: number,
        // eslint-disable-next-line no-unused-vars
        seekRange?: ISeekRange,
    ) => {
        if (stats) {
            QoEAppLogger.updateProfileTime(stats);
            QoEAppLogger.updateAllHealthStatsMetrics(stats);
            QoEAppLogger.updateMetricsPlayPositionAndBuffer(playerCurrentTime);
            QoEAppLogger.updateProfilePlayingTime();
        }
        QoEAppLogger.setEndReason(data);
        // only end of content should set isProgramBoundary = true
        if (reason === QoEAppLogger.PlaybackEndReasonType.END_OF_CONTENT)
            QoEAppLogger.isProgramBoundary = true;
        else if (
            reason !== QoEAppLogger.PlaybackEndReasonType.UNSUBSCRIBED ||
            reason !== QoEAppLogger.PlaybackEndReasonType.BLACKLIST
        )
            QoEAppLogger.logStreamingEndEvent();
        // watchContentEnd should not be called on audio track changed.
        if (reason !== QoEAppLogger.StreamEndReason.AUDIO_TRACK_CHANGED)
            QoEAppLogger.logWatchContentEnd();

        if (
            reason !== QoEAppLogger.StreamEndReason.AUDIO_TRACK_CHANGED &&
            reason !== QoEAppLogger.PlaybackEndReasonType.END_OF_CONTENT &&
            reason !== QoEAppLogger.PlaybackEndReasonType.LOST_NETWORK
        ) {
            QoEAppLogger.clearAllAttributes();
            QoEAppLogger.resetProfilePlayingTime();
            QoEAppLogger.previousPlayerEvent = null;
        }

        if (
            reason === QoEAppLogger.PlaybackEndReasonType.CHANNEL_CHANGE ||
            reason === QoEAppLogger.PlaybackEndReasonType.ENDED_BY_USER
        ) {
            QoEAppLogger.state.videoPlayerURL = '';
            QoEAppLogger.content.drmType = '';
            QoEAppLogger.content.containerFmt = QoEContainerFormatType.UNKNOWN;
        }
    };

    // ********************************************************************
    // set QOE params global - {base, population}
    //                content
    //                metrics
    //                state
    // ********************************************************************

    private static getMetricsPayload() {
        return new Payload(QoEAppLogger.mediaStats);
    }

    private static getEndStatusPayload() {
        return new Payload(QoEAppLogger.endStatus);
    }

    private static getContentPayload() {
        return new Payload(QoEAppLogger.content);
    }

    private static getPlayerStatePayload() {
        return new Payload(QoEAppLogger.state);
    }

    public static createProfiles = (variants) => {
        if (variants?.length) {
            variants.forEach((variant) => {
                const obj: Profile = {
                    width: variant.width,
                    height: variant.height,
                    playTime: 0,
                    ratio: Math.floor(variant.width / variant.height),
                    bandwidth: variant.bandwidth,
                };
                QoEAppLogger.profiles.push(obj);
            });

            let profilesArray = [...QoEAppLogger.profiles];
            profilesArray = profilesArray.filter(
                (profile: Profile, index: number, arr: Profile[]) => {
                    return (
                        index === arr.findIndex((p: Profile) => p.bandwidth === profile.bandwidth)
                    );
                },
            );

            QoEAppLogger.profiles = [...profilesArray];
        }
    };

    public static updateProfileTime = (variant?: any, timeStamp?: number) => {
        const { stats } = QoEAppLogger.playerInfo || {};
        const variantObj = variant || stats;
        const intervalTime =
            QoEAppLogger.mediaHealthStartIntervalTime || QoEAppLogger.watchContentStartTime;
        const time = timeStamp || Date.now() - intervalTime;
        if (variantObj && QoEAppLogger.profiles.length) {
            const variantBandwidth = variantObj.streamBandwidth || variantObj.bandwidth;
            QoEAppLogger.profiles.forEach((profile: Profile, index) => {
                if (variantBandwidth && variantBandwidth === profile.bandwidth) {
                    QoEAppLogger.profiles[index].playTime += time;
                }
            });
        }
    };

    private static getContainerFmt(containerFmt: TransportEncodingType) {
        switch (containerFmt) {
            case TransportEncodingType.DashFmp4TransportStream:
                return QoEContainerFormatType.DASHFMP4TS;
            case TransportEncodingType.DashTransportStream:
                return QoEContainerFormatType.DASHTS;
            case TransportEncodingType.HlsFmp4TransportStream:
                return QoEContainerFormatType.HLSFMP4TS;
            case TransportEncodingType.Mpeg2TransportStream:
                return QoEContainerFormatType.MPEG;
            case TransportEncodingType.HlsTransportStream:
                return QoEContainerFormatType.HLSTS;
            case TransportEncodingType.SmoothTransportStream:
                return QoEContainerFormatType.SMOOTHTS;
            default:
                return QoEContainerFormatType.UNKNOWN;
        }
    }

    private static getStreamingProtocol(url = '') {
        let protocol = QoEStreamingProtocolType.UNKNOWN;
        if (url.includes(DASH_TRANSPORT_STREAM)) {
            protocol = QoEStreamingProtocolType.DASH;
        } else if (url.includes(HLS_TRANSPORT_STREAM)) {
            protocol = QoEStreamingProtocolType.HLS;
        } else if (url.includes(SMOOTH_TRANSPORT_STREAM)) {
            protocol = QoEStreamingProtocolType.SMOOTH_STREAMING;
        }
        return protocol;
    }

    private static getPlayerConfigurationPayload() {
        QoEAppLogger.configuration.videoPlayer = this.configuration.videoPlayer;
        QoEAppLogger.configuration.videoSource = this.configuration.videoSource;
        QoEAppLogger.configuration.assetBounds = this.configuration.assetBounds;
        QoEAppLogger.configuration.assetBounds = this.configuration.assetBounds;
        QoEAppLogger.configuration.streamType = this.configuration.streamType;
        QoEAppLogger.configuration.connection =
            DeviceModel.getTVProperties().getActiveConnectionType();
        return new Payload(QoEAppLogger.configuration);
    }

    public static getWatchContentEndReason = () => {
        return QoEAppLogger.watchContentEndReason;
    };

    private static clearStatusPayload() {
        QoEAppLogger.endStatus.errorCode = '';
        QoEAppLogger.endStatus.errorLoc = '';
        QoEAppLogger.endStatus.errorType = '';
        QoEAppLogger.endStatus.errorNative = '';
        QoEAppLogger.endStatus.errorMsg = '';
        QoEAppLogger.endStatus.errorUrl = '';
        QoEAppLogger.endStatus.status = '';
        QoEAppLogger.endStatus.reason = '';
        QoEAppLogger.endStatus.doneReason = '';
        QoEAppLogger.endStatus.userTerminalCodes = '';
        QoEAppLogger.endStatus.userFinalCodes = '';
    }

    public static setWatchContentEndReason = (value: string) => {
        QoEAppLogger.watchContentEndReason = value;
    };

    public static updateStatusPayload(data: IQoEStatus) {
        const {
            errorCode,
            errorLoc,
            errorMsg,
            errorType,
            errorNative,
            status,
            reason,
            userAgent,
            doneReason,
            userFinalCodes,
            userTerminalCodes,
        } = data;
        QoEAppLogger.clearStatusPayload();
        QoEAppLogger.endStatus.errorCode = errorCode;
        QoEAppLogger.endStatus.errorLoc = errorLoc;
        QoEAppLogger.endStatus.errorType = errorType;
        QoEAppLogger.endStatus.errorNative = errorNative;
        QoEAppLogger.endStatus.errorMsg = errorMsg;
        QoEAppLogger.endStatus.errorUrl = QoEAppLogger.state.videoPlayerURL;
        QoEAppLogger.endStatus.status = status;
        QoEAppLogger.endStatus.reason = reason;
        QoEAppLogger.endStatus.doneReason = doneReason;
        QoEAppLogger.endStatus.userTerminalCodes = userTerminalCodes;
        QoEAppLogger.endStatus.userFinalCodes = userFinalCodes;
        QoEAppLogger.endStatus.userAgent = userAgent;
    }

    public static updateMetricsPlayPositionAndBuffer(playerCurrentTime: number) {
        const currentTime = playerCurrentTime || 0;
        QoEAppLogger.mediaStats.playPosition = Math.floor(currentTime);
        QoEAppLogger.mediaStats.videoBufferAvailable = QoEAppLogger.getBufferedInfo();
    }

    public static updateMetricsVideoBufferAvailable(availableBufferTime: number) {
        QoEAppLogger.mediaStats.videoBufferAvailable = availableBufferTime;
    }

    public static updateConfigurationForRegionMode(regionMode: QoERegionModeType) {
        QoEAppLogger.configuration.regionMode = regionMode;
    }

    public static updateConfigurationForConnectionMode(connectionMode: QoEConnectionModeType) {
        QoEAppLogger.configuration.connectionMode = connectionMode;
    }

    public static updateContentPayload(
        channelData: Channel,
        airingData: AiringOffer[],
        vodPreviewData?: TOfferPreviewCustomData,
    ) {
        QoEAppLogger.content.channelId = channelData.stationId || '';
        QoEAppLogger.content.channelName = channelData.name || '';
        QoEAppLogger.content.channelNum = <string>(channelData.channelNumber || '');
        QoEAppLogger.content.contentId =
            airingData[0]?.logging?.contentId || vodPreviewData?.contentPreview?.contentId || '';
        QoEAppLogger.content.contentTitle =
            airingData[0]?.banner?.title || vodPreviewData?.contentPreview?.title || '';
        QoEAppLogger.content.collectionId =
            airingData[0]?.logging?.collectionId ||
            vodPreviewData?.contentPreview?.collectionId ||
            '';
        QoEAppLogger.content.collectiontitle =
            airingData[0]?.banner?.title || vodPreviewData?.contentPreview?.title || '';
        QoEAppLogger.content.channelCallSign = channelData.callSign || '';
        const resolution = channelData.videoResolution || '';
        QoEAppLogger.content.maxRes = resolution.toLocaleUpperCase();
    }

    public static updateStreamingUrl(url) {
        QoEAppLogger.state.videoPlayerURL = url || '';
        QoEAppLogger.configuration.streamingProtocol = QoEAppLogger.getStreamingProtocol(
            QoEAppLogger.state.videoPlayerURL,
        );
    }

    public static updateContentPayloadFromStream(selectedAudioTrack?: IVariantTrack) {
        if (selectedAudioTrack) {
            const { videoCodec, audioCodec } = selectedAudioTrack;
            QoEAppLogger.content.videoCodec = videoCodec || '';
            QoEAppLogger.content.audioCodec = audioCodec || '';
        }
    }

    public static updateContentPayloadDrmAndContainerInfo(
        drm,
        containerFmt: TransportEncodingType,
    ) {
        QoEAppLogger.content.drmType = drm.charAt(0).toUpperCase() + drm.slice(1) || '';
        QoEAppLogger.content.containerFmt = QoEAppLogger.getContainerFmt(containerFmt);
    }

    // compute mediaHealth metrics and update

    public static updateHealthStatsMetricsForTrickPlay() {
        QoEAppLogger.isUserTriggerAction = true;
        if (!QoEAppLogger.trickplayStartTime) {
            QoEAppLogger.trickplayStartTime = Date.now();
        } else {
            QoEAppLogger.mediaStats.videoTrickplayingCount += 1;
            QoEAppLogger.mediaStats.videoPlayingCount += 1;
            QoEAppLogger.mediaStats.videoTrickplayingTime += Math.abs(
                Date.now() - QoEAppLogger.trickplayStartTime,
            );
            QoEAppLogger.trickplayStartTime = 0;
        }
    }

    public static updateHealthStatsMetricsByEvent(event: any) {
        // Ignore duplicate events
        if (
            QoEAppLogger.previousPlayerEvent &&
            event.type === QoEAppLogger.previousPlayerEvent.type
        ) {
            Log.i(
                QoEAppLogger.TAG,
                `Ignoring event: "${event.type}" because it is a duplicate of the previous event and it will not be collected.`,
            );
            return;
        }

        switch (event.type) {
            case QoEAppLogger.PlayerEvents.PLAY:
                if (QoEAppLogger.isUserTriggerAction)
                    QoEAppLogger.mediaStats.videoPlayingCount += 1;
                break;
            case QoEAppLogger.PlayerEvents.PAUSE:
                QoEAppLogger.mediaStats.videoPausingCount += 1;
                break;
            case QoEAppLogger.PlayerEvents.STALLED:
                if (QoEAppLogger.isUserTriggerAction) {
                    QoEAppLogger.mediaStats.videoBufferingCount += 1;
                } else {
                    QoEAppLogger.mediaStats.videoStallingCount += 1;
                }
                break;
            default:
        }

        QoEAppLogger.aggregateTimeDifferenceToPreviousEvent(event);
        // remember last event
        QoEAppLogger.previousPlayerEvent = event;
    }

    private static aggregateTimeDifferenceToPreviousEvent(event?: Event) {
        if (!QoEAppLogger.previousPlayerEvent) {
            // valid when function called before first event is received
            return;
        }
        const timeDiff = QoEAppLogger.getPlayerEventTimeDifference(event);
        switch (QoEAppLogger.previousPlayerEvent.type) {
            case QoEAppLogger.PlayerEvents.PAUSE:
                QoEAppLogger.mediaStats.videoPausingTime += timeDiff;
                break;
            case QoEAppLogger.PlayerEvents.STALLED:
                if (QoEAppLogger.isUserTriggerAction) {
                    QoEAppLogger.mediaStats.videoBufferingTime += timeDiff;
                } else {
                    QoEAppLogger.mediaStats.videoStallingTime += timeDiff;
                }
                break;
            default:
        }
    }

    private static getPlayerEventTimeDifference(event?: Event): number {
        let timeDiff = 0;
        if (!QoEAppLogger.previousPlayerEvent) {
            return timeDiff;
        }
        if (event?.type === QoEAppLogger.PlayerEvents.PLAY) {
            timeDiff = event.timeStamp - QoEAppLogger.previousPlayerEvent.timeStamp;
        }
        return timeDiff;
    }

    private static convertBitsToMbps(value: number) {
        return (
            Math.round((value / MILLISECONDS_IN_A_SECOND / MILLISECONDS_IN_A_SECOND) * 100) / 100
        ); // Mbps
    }

    public static updateAllHealthStatsMetrics(stats: any) {
        const decodedFrames = stats.decodedFrames ? parseInt(stats.decodedFrames, 10) : 0;
        const droppedFrames = stats.droppedFrames ? parseInt(stats.droppedFrames, 10) : 0;
        const numberOfLevelsPlayed = stats.switchHistory.length || 0;

        QoEAppLogger.mediaStats.videoFramesPresented = decodedFrames;
        QoEAppLogger.mediaStats.videoFramesDropped = droppedFrames;

        if (stats.playTime > 0) {
            QoEAppLogger.mediaStats.videoPlayingTime = Math.round(
                stats.playTime * MILLISECONDS_IN_A_SECOND,
            ); // msec
        }
        const estimatedBandwidth = stats.estimatedBandwidth || 0;
        QoEAppLogger.mediaStats.networkBitrate = QoEAppLogger.convertBitsToMbps(estimatedBandwidth);
        const videoBitrate = stats.streamBandwidth || 0;
        QoEAppLogger.mediaStats.videoBitrate = QoEAppLogger.convertBitsToMbps(videoBitrate);

        let totalLevelsBandwidth = 0;
        if (stats.switchHistory) {
            totalLevelsBandwidth = stats.switchHistory.reduce((accumulator, level) => {
                return accumulator + level.bandwidth;
            }, 0);
        }

        // Transform the size in Mb.
        // Use 1000 and NOT 1024 because of the difference between Mb (1000Kb) and MiB (1024KiB)
        // Do a check to avoid disivion by 0
        if (numberOfLevelsPlayed > 0) {
            QoEAppLogger.mediaStats.avgVideoBitrate = QoEAppLogger.convertBitsToMbps(
                totalLevelsBandwidth / numberOfLevelsPlayed,
            );
        }

        QoEAppLogger.estimatedBandwidthList.push(stats.estimatedBandwidth);
        const avgNetworkBitrate =
            QoEAppLogger.estimatedBandwidthList.reduce((a: number, b: number) => a + b, 0) /
            QoEAppLogger.estimatedBandwidthList.length;

        QoEAppLogger.mediaStats.avgNetworkBitrate =
            QoEAppLogger.convertBitsToMbps(avgNetworkBitrate);

        // collect metrics till now with dummy event
        QoEAppLogger.aggregateTimeDifferenceToPreviousEvent();
    }
}